import { Slot } from '@radix-ui/react-slot';
import type { ReactNode } from 'react';
import type { VariantProps } from '../../utils/cva';
import { tabClassNames, tabsListVariants } from '../Tabs/tabs-styles';

export interface TabsNavigationProps
  extends VariantProps<typeof tabsListVariants> {
  className?: string;
  'data-testid': string;
  children: ReactNode;
  /**
   * Same as `asChild` prop in Radix UI. The child component will be the primary component.
   *
   * @example
   * ```tsx
   * // Without asChild
   * <TabsNavigation>
   *   <ul>...</ul>
   * </TabsNavigation>
   * // With asChild
   * <TabsNavigation asChild>
   *   <ul>...</ul>
   * </TabsNavigation>
   * ```
   * ```html
   * <!-- Without asChild -->
   * <nav class="...">
   *   <ul>...</ul>
   * </nav>
   * <!-- With asChild -->
   * <ul class="...">...</ul>
   * ```
   */
  asChild?: boolean;
}

export function TabsNavigation({
  'data-testid': dataTestId,
  children,
  fixed = false,
  size = 'md',
  className,
  asChild = false,
}: TabsNavigationProps) {
  const Comp = asChild ? Slot : 'nav';
  return (
    <Comp
      data-testid={dataTestId}
      className={tabsListVariants({ fixed, size, className })}
    >
      {children}
    </Comp>
  );
}

export interface TabsNavigationLinkProps {
  className?: string;
  'data-testid': string;
  children: ReactNode;
  href: string;
  active?: boolean;
  fixed?: boolean;
  onClick?: (href: string) => void;
  LinkComponent?: React.ComponentType<{
    href: string;
    className?: string;
    'data-testid'?: string;
    'data-state'?: string;
    children: ReactNode;
  }>;
}

export function TabsNavigationLink({
  'data-testid': dataTestId,
  children,
  className,
  href,
  active = false,
  fixed = false,
  LinkComponent,
  onClick,
}: TabsNavigationLinkProps) {
  const { wrapperL0, wrapperL1, wrapperL2 } = tabClassNames({
    classNameL0: className,
    fixed,
  });

  const Comp = LinkComponent ?? 'a';

  return (
    <Comp
      data-testid={dataTestId}
      className={wrapperL0}
      href={href}
      data-state={active ? 'active' : 'inactive'}
      onClick={() => onClick?.(href)}
    >
      <div className={wrapperL1}>
        <span className={wrapperL2}>{children}</span>
      </div>
    </Comp>
  );
}
